/**
 * Client-side grouping / ranking for Discovery "Live & Current Events" rails.
 * Pure — no React / Supabase. Used by Web and Mobile.
 */

export const DISCOVERY_RAIL_CATEGORIES = ["Bars", "Clubs", "Rooftop", "Concerts"] as const;
export type DiscoveryRailCategory = (typeof DISCOVERY_RAIL_CATEGORIES)[number];

/** Pill / filter order — used as tie-break when category counts match. */
export const DISCOVERY_CATEGORY_PILL_ORDER: readonly DiscoveryRailCategory[] = [
  "Bars",
  "Clubs",
  "Rooftop",
  "Concerts",
];

export const DISCOVERY_RAIL_CHUNK_SIZE = 8;

export type DiscoveryGroupableItem = {
  id: string;
  category?: string | null;
  /** Prefer venue category when events carry both. */
  venueCategory?: string | null;
  isTopPick?: boolean;
  isPopular?: boolean;
  isNew?: boolean;
  distanceKm?: number | null;
};

export type DiscoveryCategoryRailPage<T> = {
  category: DiscoveryRailCategory;
  /** 1-based page index within this category. */
  page: number;
  pageCount: number;
  totalCount: number;
  items: T[];
};

export type DiscoveryCategoryBlock<T> = {
  category: DiscoveryRailCategory;
  totalCount: number;
  rails: DiscoveryCategoryRailPage<T>[];
};

const PILL_INDEX = new Map(
  DISCOVERY_CATEGORY_PILL_ORDER.map((c, i) => [c, i] as const),
);

export function normalizeDiscoveryRailCategory(
  raw: string | null | undefined,
): DiscoveryRailCategory | null {
  if (raw == null) return null;
  const key = String(raw).trim().toLowerCase();
  if (key === "bars" || key === "bar") return "Bars";
  if (key === "clubs" || key === "club") return "Clubs";
  if (key === "rooftop" || key === "rooftops") return "Rooftop";
  if (key === "concerts" || key === "concert" || key === "konzerte") return "Concerts";
  // Already-canonical display forms
  if (raw === "Bars" || raw === "Clubs" || raw === "Rooftop" || raw === "Concerts") {
    return raw;
  }
  return null;
}

function categoryOf(item: DiscoveryGroupableItem): DiscoveryRailCategory | null {
  return (
    normalizeDiscoveryRailCategory(item.venueCategory) ??
    normalizeDiscoveryRailCategory(item.category)
  );
}

function badgeTier(item: DiscoveryGroupableItem): number {
  if (item.isTopPick) return 0;
  if (item.isPopular) return 1;
  if (item.isNew) return 2;
  return 3;
}

function distanceOrEnd(item: DiscoveryGroupableItem): number {
  const d = item.distanceKm;
  if (d == null || !Number.isFinite(d) || d < 0) return Number.POSITIVE_INFINITY;
  return d;
}

function sortBadgeThenDistance<T extends DiscoveryGroupableItem>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const tierDiff = badgeTier(a.item) - badgeTier(b.item);
      if (tierDiff !== 0) return tierDiff;
      const distDiff = distanceOrEnd(a.item) - distanceOrEnd(b.item);
      if (distDiff !== 0) return distDiff;
      return a.index - b.index;
    })
    .map((e) => e.item);
}

function sortDistanceOnly<T extends DiscoveryGroupableItem>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const distDiff = distanceOrEnd(a.item) - distanceOrEnd(b.item);
      if (distDiff !== 0) return distDiff;
      return a.index - b.index;
    })
    .map((e) => e.item);
}

function chunk<T>(items: T[], size: number): T[][] {
  if (items.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Group discovery items by venue category, rank for rail 1 (badge → distance),
 * then chunk remainders by distance. Category blocks ordered by count desc
 * (pill-order tie-break). Empty / unknown categories omitted.
 */
export function groupAndRankDiscoveryItems<T extends DiscoveryGroupableItem>(
  items: readonly T[],
  options?: { chunkSize?: number },
): DiscoveryCategoryBlock<T>[] {
  const chunkSize = options?.chunkSize ?? DISCOVERY_RAIL_CHUNK_SIZE;
  if (chunkSize <= 0) return [];

  const buckets = new Map<DiscoveryRailCategory, T[]>();
  for (const item of items) {
    const cat = categoryOf(item);
    if (!cat) continue;
    const list = buckets.get(cat);
    if (list) list.push(item);
    else buckets.set(cat, [item]);
  }

  const blocks: DiscoveryCategoryBlock<T>[] = [];

  for (const category of DISCOVERY_CATEGORY_PILL_ORDER) {
    const raw = buckets.get(category);
    if (!raw || raw.length === 0) continue;

    const rankedRail1 = sortBadgeThenDistance(raw);
    const first = rankedRail1.slice(0, chunkSize);
    const rest = sortDistanceOnly(rankedRail1.slice(chunkSize));
    const pages = [first, ...chunk(rest, chunkSize)].filter((p) => p.length > 0);
    const pageCount = pages.length;

    blocks.push({
      category,
      totalCount: raw.length,
      rails: pages.map((pageItems, i) => ({
        category,
        page: i + 1,
        pageCount,
        totalCount: raw.length,
        items: pageItems,
      })),
    });
  }

  blocks.sort((a, b) => {
    const countDiff = b.totalCount - a.totalCount;
    if (countDiff !== 0) return countDiff;
    return (PILL_INDEX.get(a.category) ?? 99) - (PILL_INDEX.get(b.category) ?? 99);
  });

  return blocks;
}

const EARTH_RADIUS_KM = 6371;

/** Great-circle distance in km (for enriching mobile items before grouping). */
export function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Common Pulse city centers for distance ranking when GPS is unavailable. */
export const DISCOVERY_CITY_CENTERS: Record<string, { lat: number; lng: number }> = {
  zurich: { lat: 47.3769, lng: 8.5417 },
  zürich: { lat: 47.3769, lng: 8.5417 },
  bern: { lat: 46.948, lng: 7.4474 },
  basel: { lat: 47.5596, lng: 7.5886 },
  geneva: { lat: 46.2044, lng: 6.1432 },
  genève: { lat: 46.2044, lng: 6.1432 },
  lausanne: { lat: 46.5197, lng: 6.6323 },
  lucerne: { lat: 47.0502, lng: 8.3093 },
  luzern: { lat: 47.0502, lng: 8.3093 },
};

export function getDiscoveryCityCenter(
  city: string,
): { lat: number; lng: number } | null {
  const key = city.trim().toLowerCase();
  return DISCOVERY_CITY_CENTERS[key] ?? null;
}

export type DistanceEnrichable = {
  distanceKm?: number | null;
  venueLat?: number | null;
  venueLng?: number | null;
  lat?: number | null;
  lng?: number | null;
};

/**
 * Attach distanceKm from origin (user or city center). Leaves existing finite
 * distanceKm untouched. Missing coords → distanceKm stays undefined.
 */
export function enrichItemsWithDistanceKm<T extends DistanceEnrichable>(
  items: readonly T[],
  origin: { lat: number; lng: number } | null | undefined,
): T[] {
  if (!origin) return items.map((item) => ({ ...item }));
  return items.map((item) => {
    if (item.distanceKm != null && Number.isFinite(item.distanceKm) && item.distanceKm >= 0) {
      return item;
    }
    const lat = item.venueLat ?? item.lat;
    const lng = item.venueLng ?? item.lng;
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return item;
    }
    return { ...item, distanceKm: haversineKm(origin.lat, origin.lng, lat, lng) };
  });
}

export type TeaserSplitCounts = {
  /** Full (non-teaser) items. */
  visible: number;
  /** Full + teaser items. */
  total: number;
};

/** Count full vs teaser rows in an already filter-scoped list (client-side only). */
export function countTeaserSplit<T extends { isTeaser?: boolean }>(
  items: readonly T[],
): TeaserSplitCounts {
  let visible = 0;
  for (const item of items) {
    if (!item.isTeaser) visible += 1;
  }
  return { visible, total: items.length };
}

/** True when the scoped list still contains freemium teaser rows. */
export function hasTeaserCapHidden(venues: TeaserSplitCounts, events: TeaserSplitCounts): boolean {
  return venues.total > venues.visible || events.total > events.visible;
}
